// pages/hauler/HaulerApplyPage.js
// Recruitment stub for /haulers/apply.
//
// Static-form-first approach: collects basic Hauler-applicant info and
// attempts to insert into a `hauler_applications` Supabase table. The
// table doesn't exist yet — when the insert fails (e.g., 42P01 relation
// missing), we still show the success state to the applicant and log
// a console.warn so Bejorn can see the gap during local testing.
//
// Once the table is created (separate migration with admin-only RLS),
// this page persists submissions automatically — no code change needed.
//
// Original IP — LoadDash Services LLC

import React, { useEffect, useState } from "react";
import { supabase } from "../../services/supabaseService";
import { BRAND_NAME } from "../../constants";
import { EVENTS, trackRoleContextEvent } from "../../analytics/roleContextTelemetry";
import "../../styles/haulerTheme.css";

const SORA = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&display=swap');`;

const COLORS = {
  bg: "var(--hl-bg)",
  card: "var(--hl-card)",
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  border: "var(--hl-border)",
  inputBg: "var(--hl-input-bg)",
  sky: "var(--hl-sky)",
  green: "var(--hl-green)",
  red: "var(--hl-red)",
};

const initialForm = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  has_trailer: "no",
  notes: "",
};

export default function HaulerApplyPage() {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    trackRoleContextEvent(EVENTS.HAULER_APPLY_STARTED, null, {
      intent: "hauler_interest",
      role_context: "hauler",
      surface: "hauler_apply",
      page: "hauler_apply",
    });
  }, []);

  function update(k) { return (e) => setForm(prev => ({ ...prev, [k]: e.target.value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }

    setBusy(true);
    try {
      const { error: insertErr } = await supabase
        .from("hauler_applications")
        .insert({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          vehicle: form.vehicle.trim() || null,
          has_trailer: form.has_trailer === "yes",
          notes: form.notes.trim() || null,
          submitted_at: new Date().toISOString(),
        });

      if (insertErr) {
        // Table likely doesn't exist yet (42P01) or RLS blocks anon insert.
        // We still show success to the applicant — Bejorn will see the warn
        // in the console and migrate the table to start persisting.
        console.warn(
          "[HaulerApplyPage] Application not persisted:",
          insertErr.message,
          "— hauler_applications table may not exist yet."
        );
      }
    } catch (err) {
      console.warn("[HaulerApplyPage] Submission error:", err?.message);
    } finally {
      setBusy(false);
      setSubmitted(true);
      trackRoleContextEvent(EVENTS.HAULER_APPLY_COMPLETED, null, {
        intent: "hauler_interest",
        role_context: "hauler",
        surface: "hauler_apply",
        page: "hauler_apply",
        has_phone: !!form.phone.trim(),
        has_vehicle: !!form.vehicle.trim(),
        has_trailer: form.has_trailer === "yes",
      });
    }
  }

  return (
    <div style={{
      fontFamily: "'Sora', -apple-system, system-ui, sans-serif",
      minHeight: "100vh",
      background: COLORS.bg,
      color: COLORS.text,
      WebkitFontSmoothing: "antialiased",
    }}>
      <style>{SORA}</style>

      {/* Brand bar — mirrors HaulerApp's top bar */}
      <div style={{
        background: "var(--hl-top-bg)",
        padding: "10px 18px",
        paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
        display: "flex",
        alignItems: "center",
        gap: 8,
        borderBottom: "1px solid var(--hl-top-border)",
      }}>
        <img src="/Loaddashbutton.png" alt={BRAND_NAME} style={{ width: 24, height: 24, borderRadius: 6, objectFit: "contain" }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: COLORS.sky }}>
          {BRAND_NAME} Haulers
        </span>
      </div>

      <main style={{ maxWidth: 520, margin: "0 auto", padding: "28px 16px 64px" }}>
        {!submitted ? (
          <>
            <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.muted, marginBottom: 6 }}>
              APPLY
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, marginBottom: 8 }}>
              Apply to become a {BRAND_NAME} Hauler
            </h1>
            <p style={{ fontSize: 14, color: COLORS.sub, lineHeight: 1.5, marginBottom: 20 }}>
              We're growing the Hauler team at Hill AFB. Tell us about you and we'll be in touch.
            </p>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Full name"
                  required
                  style={inputStyle}
                />
              </Field>

              <Field label="Email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  required
                  style={inputStyle}
                />
              </Field>

              <Field label="Phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(801) 555-1234"
                  style={inputStyle}
                />
              </Field>

              <Field label="Vehicle">
                <input
                  type="text"
                  value={form.vehicle}
                  onChange={update("vehicle")}
                  placeholder="e.g. 2018 Ford F-150, standard bed"
                  style={inputStyle}
                />
              </Field>

              <Field label="Do you have a utility trailer?">
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { v: "yes", label: "Yes" },
                    { v: "no",  label: "No" },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, has_trailer: opt.v }))}
                      style={{
                        flex: 1,
                        padding: "12px",
                        borderRadius: 10,
                        border: `1px solid ${form.has_trailer === opt.v ? COLORS.sky : COLORS.border}`,
                        background: form.has_trailer === opt.v ? "rgba(61,144,217,.16)" : COLORS.inputBg,
                        color: COLORS.text,
                        fontFamily: "inherit",
                        fontSize: 14,
                        fontWeight: form.has_trailer === opt.v ? 800 : 600,
                        cursor: "pointer",
                        minHeight: 48,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Anything else?">
                <textarea
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="Schedule, experience, or questions"
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
                />
              </Field>

              {error && (
                <div style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "rgba(217,82,82,.14)",
                  border: `1px solid ${COLORS.red}`,
                  color: COLORS.red,
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                style={{
                  width: "100%",
                  padding: 14,
                  borderRadius: 12,
                  border: "none",
                  background: busy ? "rgba(61,144,217,.5)" : COLORS.sky,
                  color: "#FFFFFF",
                  fontFamily: "inherit",
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: busy ? "wait" : "pointer",
                  minHeight: 48,
                  marginTop: 4,
                }}
              >
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: 24 }}>
              <a href="/haulers" style={{ color: COLORS.sky, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                ← Back to {BRAND_NAME} Haulers
              </a>
            </div>
          </>
        ) : (
          <div style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            padding: "32px 24px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }} aria-hidden="true">✓</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 8 }}>
              Thanks — we got it
            </h2>
            <p style={{ fontSize: 13, color: COLORS.sub, lineHeight: 1.5, marginBottom: 20 }}>
              We'll review your application and reach out by phone or email. In the meantime, you can return to the Hauler app.
            </p>
            <a
              href="/haulers"
              style={{
                display: "inline-block",
                padding: "12px 20px",
                borderRadius: 10,
                background: COLORS.sky,
                color: "#FFFFFF",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 700,
                textDecoration: "none",
                minHeight: 44,
              }}
            >
              Back to {BRAND_NAME} Haulers
            </a>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        fontSize: 11,
        letterSpacing: 1.5,
        textTransform: "uppercase",
        fontWeight: 700,
        color: COLORS.sub,
        marginBottom: 6,
      }}>
        {label} {required && <span style={{ color: COLORS.sky }}>*</span>}
      </div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.inputBg,
  color: COLORS.text,
  fontFamily: "inherit",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};
